/**
 * TSM Ranking Report Renderer
 *
 * Company-wide Service Advisor leaderboard:
 *  - Per-metric leaders strip
 *  - Full ranked table sorted by total score ascending
 *  - Per-location aggregate (expandable)
 *
 * Designed to be printed in landscape and posted on a physical bulletin board.
 *
 * Presentational sub-components (`MetricLeaders`, `RankedTable`,
 * `LocationAggregateTable`, formatters) live in `TsmRankingShared.tsx` and are
 * reused by the sibling `TsmGroupRankingReport.tsx`.
 */

import React, { useMemo } from 'react';
import {
  fmtAsOf,
  LocationAggregateTable,
  MetricLeader,
  MetricLeaders,
  MultiLocationExcludedAdvisor,
  RankedTable,
  TsmLocationRow,
  TsmRankedRow,
} from './TsmRankingShared';

// Re-export shared types for any downstream importers that pulled them from
// this module historically.
export type {
  MetricKey,
  TsmRankedRow,
  MetricLeader,
  MultiLocationExcludedAdvisor,
  TsmLocationRow,
} from './TsmRankingShared';

export interface TsmRankingReportData {
  reportMonth: { year: number; month: number; monthName: string };
  asOf: string | null;
  totalAdvisors: number;
  podium: TsmRankedRow[];
  metricLeaders: MetricLeader[];
  rows: TsmRankedRow[];
  multiLocationExcluded?: MultiLocationExcludedAdvisor[];
  locationRows?: TsmLocationRow[];
}

export const TsmRankingReport: React.FC<{ data: TsmRankingReportData }> = ({ data }) => {
  const tsmRowsByLocation = useMemo(() => {
    const map = new Map<number, TsmRankedRow[]>();
    if (data?.rows) {
      for (const r of data.rows) {
        const list = map.get(r.locationId) ?? [];
        list.push(r);
        map.set(r.locationId, list);
      }
      map.forEach((list) => {
        list.sort((a, b) => a.totalScore - b.totalScore);
      });
    }
    return map;
  }, [data?.rows]);

  if (!data || data.rows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No Service Advisor activity found for {data?.reportMonth?.monthName ?? 'this period'}.
      </div>
    );
  }

  return (
    <div className="text-gray-900">
      {/* Header */}
      <div className="mb-6 print:mb-4 print:break-inside-avoid">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 print:text-xl">TSM Ranking Report</h1>
            <p className="text-sm text-gray-600 mt-1">
              {data.reportMonth.monthName} {data.reportMonth.year} · {data.totalAdvisors} Service Advisors ranked
              company-wide
            </p>
          </div>
          <div className="text-xs text-gray-500">
            Snapshot as of {fmtAsOf(data.asOf)}
          </div>
        </div>
      </div>

      {/* Per-metric leaders */}
      <MetricLeaders leaders={data.metricLeaders} />

      {/* Full ranked table */}
      <div className="print:break-inside-auto">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Full Ranking
        </div>
        <RankedTable rows={data.rows} />
      </div>

      {/* Per-location aggregate table */}
      {data.locationRows && data.locationRows.length > 0 && (
        <div className="mt-10 print:mt-8 print:break-before-page">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            By Location <span className="text-gray-400 normal-case font-normal">(click a row to expand its TSMs)</span>
          </div>
          <LocationAggregateTable
            locationRows={data.locationRows}
            tsmRowsByLocation={tsmRowsByLocation}
            tsmTotal={data.rows.length}
          />
        </div>
      )}

      {/* Multi-location fill-in advisor footnote */}
      {data.multiLocationExcluded && data.multiLocationExcluded.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 leading-relaxed print:break-inside-avoid">
          <strong className="text-gray-700">Excluded — multi-location fill-in advisors:</strong>{' '}
          {data.multiLocationExcluded
            .map((a) => `${a.advisorName} (covered ${a.locations.join(', ')})`)
            .join('; ')}
          .{' '}
          <span className="italic">
            These TSMs wrote ROs at 2+ locations this month — typically covering vacations across the
            company — so they're removed from the head-to-head rankings.
          </span>
        </div>
      )}

      {/* Methodology */}
      <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 leading-relaxed print:break-inside-avoid">
        <strong className="text-gray-700">How scoring works:</strong> Each advisor is ranked 1..{data.totalAdvisors} on
        six metrics (lower rank = better performance). Standard competition ranking — ties share the lower rank, the
        next advisor jumps the gap (1, 2, 2, 4). The Total Score sums <em>all six</em> ranks. <em>Lower score wins.</em>
        {' '}The Closed/Open ROs column shows closed and open RO counts (e.g. 100/33) and is ranked by fewest open ROs.
        {' '}Metrics: Open ROs (fewer open, scored), Oldest Open RO Age (younger, scored),
        Customer Pay + Service Contract Gross (higher, scored), Avg Flat-Rate Hours per Customer Pay RO (higher, scored),
        Total Labor Gross all pay types (higher, scored), Customer Pay ELR (higher, scored). Locations 12 and 44 are
        Body Shop and excluded. To qualify, an advisor must have written at least
        1 RO (open or closed) per Service working day of the month at their location — e.g. on working day 16 they need
        ≥ 16 ROs; for a completed past month they need ≥ the month's full working-day count. Loc 5 &amp; Loc 11 are Service-closed on
        Saturdays so their threshold is lower than the other six. This drops assistants, occasional RO writers, and new hires
        with too little activity to compete.
        {' '}The <em>By Location</em> table below ranks the 8 locations on the same 6 aggregate metrics plus a 7th — Controllable
        Expense % of Gross (062D + 067D + 069D + 069DL ÷ service gross, same source as the Forecast page Service Department Analysis card,
        lower is better). Location-row <em>Avg Hrs/RO (CP+SC)</em> is sourced directly from the GL (450* labor sales accounts, per-location
        filter to exclude body-shop suffixes) — true store-wide pool of CP+SC hours ÷ distinct ROs that posted CP/SC labor revenue. Loc 1's
        CVC accounts are included because that "body shop" is actually an internal service center; Loc 4's real body shop is excluded.
        Location aggregates include all advisor activity, including fill-ins.
      </div>
    </div>
  );
};
